/**
 * Catalogo Natura IVA → RiferimentoNormativo → Bollo virtuale (mapping v3).
 *
 * FONTE DI VERITÀ unica (lato codice) del mapping tra codice `<Natura>` SDI,
 * testo `<RiferimentoNormativo>` FatturaPA e obbligo di bollo virtuale. La
 * tabella DB gemella `public.natura_iva_map` (migration omonima) ne è il
 * riflesso persistito e read-only (pattern Task #2903). Consumato da FE
 * (cascata aliquota 0%) + backend (mapper A-Cube).
 *
 * Fonti normative: DPR 633/1972 · DL 41/1995 · DL 331/1993 · L.190/2014 ·
 * specifiche tecniche FatturaPA v1.9.
 *
 * REGOLE INVARIANTI (vedi docs/fiscal-natura-iva-map.md):
 *   1. La dipendenza funzionale del bollo è sulla COPPIA `(natura, riferimento)`,
 *      MAI sul solo codice natura: N2.2 forfettario → bollo SÌ, N2.2 monofase → NO.
 *   2. `<RiferimentoNormativo>` è obbligatorio quando `AliquotaIVA = 0.00`.
 *   3. N4 deve sempre riportare il numero dell'art.10 (27 numeri diversi).
 *   4. N3.5 richiede `<AltriDatiGestionali>` con `<TipoDato>INTENTO</TipoDato>`.
 *   5. N6.x richiede annotazione "inversione contabile" in fattura.
 *   6. N5 (regime del margine) → `<Imposta>0.00</Imposta>`, IVA non esposta.
 */

/**
 * Codici `<Natura>` — mirror dell'enum `public.pos_vat_nature` (24 valori).
 * I codici interi N2/N3/N6 sono del tracciato corrispettivi RT v7.0 (che NON
 * ammette sotto-codici); i puntati sono del tracciato fattura SDI.
 */
export const POS_VAT_NATURE_CODES = [
  'N1',
  'N2',
  'N2.1',
  'N2.2',
  'N3',
  'N3.1',
  'N3.2',
  'N3.3',
  'N3.4',
  'N3.5',
  'N3.6',
  'N4',
  'N5',
  'N6',
  'N6.1',
  'N6.2',
  'N6.3',
  'N6.4',
  'N6.5',
  'N6.6',
  'N6.7',
  'N6.8',
  'N6.9',
  'N7',
] as const;
export type PosVatNatureCode = (typeof POS_VAT_NATURE_CODES)[number];

/** Macro-categorie descrittive per il raggruppamento UI della cascata. */
export const NATURA_IVA_CATEGORIES = [
  'Escluse art. 15',
  'Non soggette - territorialità',
  'Non soggette - altri casi',
  'Non imponibili - esportazioni',
  'Non imponibili - intracomunitarie',
  'Non imponibili - San Marino',
  'Non imponibili - assimilate export',
  "Non imponibili - dichiarazione d'intento",
  'Non imponibili - altri casi',
  'Esenti art. 10',
  'Regime del margine',
  'Reverse charge',
  'IVA assolta in altro Stato UE',
] as const;
export type NaturaIvaCategory = (typeof NATURA_IVA_CATEGORIES)[number];

/** Voce del catalogo: coppia (natura, riferimento) → bollo + metadati. */
export interface NaturaIvaMapEntry {
  /** Ordinale stabile 1..79 della mappatura ufficiale v3. PK del catalogo. */
  readonly id: number;
  /** Codice `<Natura>` SDI. */
  readonly natura: PosVatNatureCode;
  /** Testo `<RiferimentoNormativo>` FatturaPA (≤100 char nel tracciato SDI). */
  readonly riferimentoNormativo: string;
  /** Bollo virtuale €2,00 dovuto se l'imponibile dell'operazione supera €77,47. */
  readonly bolloOltreSoglia: boolean;
  /** Macro-categoria descrittiva (raggruppamento UI). */
  readonly categoria: NaturaIvaCategory;
  /** Nota operativa obbligatoria (INTENTO, inversione contabile, margine...). */
  readonly nota?: string;
}

const NOTA_INTENTO =
  "Inserire il protocollo della dichiarazione d'intento in <AltriDatiGestionali> con <TipoDato>INTENTO</TipoDato>.";
const NOTA_MARGINE =
  'Regime del margine: IVA non esposta (<Imposta>0.00</Imposta>). Annotazione obbligatoria in fattura.';
const NOTA_REVERSE = 'Annotazione obbligatoria in fattura: "inversione contabile".';

/** 79 righe — mapping v3. Ordine = ordinale ufficiale. */
export const NATURA_IVA_MAP: ReadonlyArray<NaturaIvaMapEntry> = [
  // N1 — Escluse ex art. 15 DPR 633/1972
  { id: 1, natura: 'N1', riferimentoNormativo: 'Art. 15 c.1 n.1 DPR 633/1972 - interessi moratori e penali', bolloOltreSoglia: true, categoria: 'Escluse art. 15' },
  { id: 2, natura: 'N1', riferimentoNormativo: 'Art. 15 c.1 n.2 DPR 633/1972 - sconto/premio/abbuono in natura', bolloOltreSoglia: true, categoria: 'Escluse art. 15' },
  { id: 3, natura: 'N1', riferimentoNormativo: 'Art. 15 c.1 n.3 DPR 633/1972 - anticipazioni nome e per conto', bolloOltreSoglia: true, categoria: 'Escluse art. 15' },
  { id: 4, natura: 'N1', riferimentoNormativo: 'Art. 15 c.2 DPR 633/1972 - imballaggi a rendere con cauzione', bolloOltreSoglia: true, categoria: 'Escluse art. 15' },
  { id: 5, natura: 'N1', riferimentoNormativo: 'Art. 15 c.5 DPR 633/1972 - rivalsa IVA', bolloOltreSoglia: false, categoria: 'Escluse art. 15' },

  // N2.1 — Non soggette per carenza territorialità (artt. 7-bis → 7-septies)
  { id: 6, natura: 'N2.1', riferimentoNormativo: 'Art. 7-bis DPR 633/1972 - beni non presenti in Italia', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },
  { id: 7, natura: 'N2.1', riferimentoNormativo: 'Art. 7-ter DPR 633/1972 - servizi a soggetti passivi B2B', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },
  { id: 8, natura: 'N2.1', riferimentoNormativo: 'Art. 7-quater DPR 633/1972 - servizi speciali derogatori', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },
  { id: 9, natura: 'N2.1', riferimentoNormativo: 'Art. 7-quinquies DPR 633/1972 - cultura sport istruzione', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },
  { id: 10, natura: 'N2.1', riferimentoNormativo: 'Art. 7-sexies DPR 633/1972 - noleggio e telecom B2C', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },
  { id: 11, natura: 'N2.1', riferimentoNormativo: 'Art. 7-septies DPR 633/1972 - servizi specifici a privati extra-UE', bolloOltreSoglia: true, categoria: 'Non soggette - territorialità' },

  // N2.2 — Non soggette altri casi (fuori campo, regimi agevolati, monofase, intrattenimenti)
  { id: 12, natura: 'N2.2', riferimentoNormativo: 'Artt. 2, 3, 4, 5 DPR 633/1972 - fuori campo oggettivo/soggettivo', bolloOltreSoglia: true, categoria: 'Non soggette - altri casi' },
  { id: 13, natura: 'N2.2', riferimentoNormativo: 'Art. 1 cc.54-89 L.190/2014 - regime forfettario', bolloOltreSoglia: true, categoria: 'Non soggette - altri casi' },
  { id: 14, natura: 'N2.2', riferimentoNormativo: 'Art. 27 cc.1-2 DL 98/2011 - contribuenti minimi', bolloOltreSoglia: true, categoria: 'Non soggette - altri casi' },
  { id: 15, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.a DPR 633/1972 - sali e tabacchi monofase rivenditore', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 16, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.b DPR 633/1972 - fiammiferi monofase rivenditore', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 17, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.c DPR 633/1972 - editoria monofase rivenditore', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 18, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.d DPR 633/1972 - gestori telefoni pubblici monofase', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 19, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.d DPR 633/1972 - telefonia mezzi tecnici monofase dealer', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 20, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.d DPR 633/1972 - servizi telematici monofase', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 21, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.1 lett.e DPR 633/1972 - biglietti trasporto e sosta monofase', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 22, natura: 'N2.2', riferimentoNormativo: 'Art. 74 c.6 DPR 633/1972 - intrattenimenti e giochi', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 23, natura: 'N2.2', riferimentoNormativo: 'DM 9/04/1983 - commercio quotidiani periodici libri rivenditori', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 24, natura: 'N2.2', riferimentoNormativo: 'Art. 26 c.3 DPR 633/1972 - variazione fuori campo', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 25, natura: 'N2.2', riferimentoNormativo: 'Art. 38 c.5 DL 331/1993 - deposito IVA introduzione beni', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },
  { id: 26, natura: 'N2.2', riferimentoNormativo: 'Art. 50-bis c.4 DL 331/1993 - deposito IVA operazioni esteri', bolloOltreSoglia: false, categoria: 'Non soggette - altri casi' },

  // N3.1 — Non imponibili: esportazioni
  { id: 27, natura: 'N3.1', riferimentoNormativo: 'Art. 8 c.1 lett.a DPR 633/1972 - esportazione diretta', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },
  { id: 28, natura: 'N3.1', riferimentoNormativo: 'Art. 8 c.1 lett.a DPR 633/1972 - esportazione indiretta cessionario', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },
  { id: 29, natura: 'N3.1', riferimentoNormativo: 'Art. 8 c.1 lett.b-bis DPR 633/1972', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },
  { id: 30, natura: 'N3.1', riferimentoNormativo: 'Art. 8-bis c.2 DPR 633/1972 - provviste di bordo', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },
  { id: 31, natura: 'N3.1', riferimentoNormativo: 'Art. 38-quater c.1 DPR 633/1972 - tax free', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },
  { id: 32, natura: 'N3.1', riferimentoNormativo: 'Art. 58 c.1 DL 331/1993 - triangolazione', bolloOltreSoglia: false, categoria: 'Non imponibili - esportazioni' },

  // N3.2 — Non imponibili: cessioni intracomunitarie
  { id: 33, natura: 'N3.2', riferimentoNormativo: 'Art. 41 DL 331/1993 - cessione intracomunitaria', bolloOltreSoglia: true, categoria: 'Non imponibili - intracomunitarie' },

  // N3.3 — Non imponibili: San Marino
  { id: 34, natura: 'N3.3', riferimentoNormativo: 'Art. 71 DPR 633/1972 - cessioni verso San Marino', bolloOltreSoglia: false, categoria: 'Non imponibili - San Marino' },

  // N3.4 — Non imponibili: operazioni assimilate esportazioni
  { id: 35, natura: 'N3.4', riferimentoNormativo: 'Art. 8-bis DPR 633/1972 - operazioni assimilate esportazioni', bolloOltreSoglia: false, categoria: 'Non imponibili - assimilate export' },

  // N3.5 — Non imponibili: dichiarazione d'intento
  { id: 36, natura: 'N3.5', riferimentoNormativo: "Art. 8 c.1 lett.c DPR 633/1972 - dichiarazione d'intento", bolloOltreSoglia: true, categoria: "Non imponibili - dichiarazione d'intento", nota: NOTA_INTENTO },

  // N3.6 — Non imponibili: altri casi
  { id: 37, natura: 'N3.6', riferimentoNormativo: 'Art. 2 c.2 n.4 DPR 633/1972 - fuori plafond', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 38, natura: 'N3.6', riferimentoNormativo: 'Art. 9 c.1 DPR 633/1972 - servizi internazionali', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 39, natura: 'N3.6', riferimentoNormativo: 'Art. 9 c.2 DPR 633/1972 - servizi beni in transito', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 40, natura: 'N3.6', riferimentoNormativo: 'Art. 14 L.49/1987 - cooperazione internazionale ONG', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 41, natura: 'N3.6', riferimentoNormativo: 'Art. 50-bis c.4 lett.f DL 331/1993 - estrazione deposito IVA UE', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 42, natura: 'N3.6', riferimentoNormativo: 'Art. 50-bis c.4 lett.g DL 331/1993 - trasferimento depositi IVA', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 43, natura: 'N3.6', riferimentoNormativo: 'Art. 72 DPR 633/1972 - organismi internazionali e diplomatici', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },
  { id: 44, natura: 'N3.6', riferimentoNormativo: 'Art. 74-ter c.8 DPR 633/1972 - pacchetti turistici quota extra-UE', bolloOltreSoglia: false, categoria: 'Non imponibili - altri casi' },

  // N4 — Esenti ex art. 10 DPR 633/1972 (numero art.10 SEMPRE obbligatorio)
  { id: 45, natura: 'N4', riferimentoNormativo: 'Art. 10 n.1 DPR 633/1972 - operazioni di credito e finanziamento', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 46, natura: 'N4', riferimentoNormativo: 'Art. 10 n.2 DPR 633/1972 - assicurazioni e riassicurazioni', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 47, natura: 'N4', riferimentoNormativo: 'Art. 10 n.3 DPR 633/1972 - valute banconote monete titoli', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 48, natura: 'N4', riferimentoNormativo: 'Art. 10 n.4 DPR 633/1972 - gestione fondi comuni', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 49, natura: 'N4', riferimentoNormativo: 'Art. 10 n.5 DPR 633/1972 - quote societarie e partecipazioni', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 50, natura: 'N4', riferimentoNormativo: 'Art. 10 n.6 DPR 633/1972 - lotterie tombole giochi scommesse', bolloOltreSoglia: false, categoria: 'Esenti art. 10' },
  { id: 51, natura: 'N4', riferimentoNormativo: 'Art. 10 n.7 DPR 633/1972 - lotto giochi rete concorsi Stato', bolloOltreSoglia: false, categoria: 'Esenti art. 10' },
  { id: 52, natura: 'N4', riferimentoNormativo: 'Art. 10 n.8 DPR 633/1972 - locazione fabbricati', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 53, natura: 'N4', riferimentoNormativo: 'Art. 10 n.8-bis DPR 633/1972 - cessione fabbricati abitativi', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 54, natura: 'N4', riferimentoNormativo: 'Art. 10 n.8-ter DPR 633/1972 - cessione fabbricati strumentali', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 55, natura: 'N4', riferimentoNormativo: 'Art. 10 n.18 DPR 633/1972 - prestazioni sanitarie', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 56, natura: 'N4', riferimentoNormativo: 'Art. 10 n.19 DPR 633/1972 - ricovero e cura', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 57, natura: 'N4', riferimentoNormativo: 'Art. 10 n.20 DPR 633/1972 - istruzione e formazione', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 58, natura: 'N4', riferimentoNormativo: 'Art. 10 n.27-ter DPR 633/1972 - servizi sociosanitari', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },
  { id: 59, natura: 'N4', riferimentoNormativo: 'Art. 19 c.3 lett.a-bis DPR 633/1972 - esente fuori pro-rata', bolloOltreSoglia: false, categoria: 'Esenti art. 10' },
  { id: 60, natura: 'N4', riferimentoNormativo: 'Art. 124 c.2 DL 34/2020 - DPI COVID', bolloOltreSoglia: true, categoria: 'Esenti art. 10' },

  // N5 — Regime del margine (IVA non esposta, Imposta=0)
  { id: 61, natura: 'N5', riferimentoNormativo: 'Regime del margine art. 36 c.1 DL 41/1995 - beni usati metodo analitico', bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },
  { id: 62, natura: 'N5', riferimentoNormativo: 'Regime del margine art. 36 c.1 DL 41/1995 - oggetti arte antiquariato collezione', bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },
  { id: 63, natura: 'N5', riferimentoNormativo: 'Regime del margine art. 36 c.5 DL 41/1995 - metodo forfettario', bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },
  { id: 64, natura: 'N5', riferimentoNormativo: 'Regime del margine art. 36 c.6 DL 41/1995 - metodo globale', bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },
  { id: 65, natura: 'N5', riferimentoNormativo: "Regime del margine art. 40-bis DL 41/1995 - agenzie vendita all'asta", bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },
  { id: 66, natura: 'N5', riferimentoNormativo: 'Regime del margine art. 74-ter DPR 633/1972 - agenzie di viaggio', bolloOltreSoglia: false, categoria: 'Regime del margine', nota: NOTA_MARGINE },

  // N6.x — Reverse charge (annotazione "inversione contabile")
  { id: 67, natura: 'N6.1', riferimentoNormativo: 'Art. 74 cc.7-8 DPR 633/1972 - rottami reverse charge', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 68, natura: 'N6.2', riferimentoNormativo: 'Art. 17 c.5 DPR 633/1972 - oro argento puro RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 69, natura: 'N6.3', riferimentoNormativo: 'Art. 17 c.6 lett.a DPR 633/1972 - subappalto edile RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 70, natura: 'N6.4', riferimentoNormativo: 'Art. 17 c.6 lett.a-bis DPR 633/1972 - fabbricati strumentali RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 71, natura: 'N6.5', riferimentoNormativo: 'Art. 17 c.6 lett.b DPR 633/1972 - telefoni cellulari RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 72, natura: 'N6.6', riferimentoNormativo: 'Art. 17 c.6 lett.c DPR 633/1972 - prodotti elettronici RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 73, natura: 'N6.7', riferimentoNormativo: 'Art. 17 c.6 lett.a-ter DPR 633/1972 - edile e settori connessi RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 74, natura: 'N6.8', riferimentoNormativo: 'Art. 17 c.6 lett.d-bis/d-ter/d-quater DPR 633/1972 - energia RC', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },
  { id: 75, natura: 'N6.9', riferimentoNormativo: 'Art. 17 c.6 DPR 633/1972 - reverse charge altri casi', bolloOltreSoglia: false, categoria: 'Reverse charge', nota: NOTA_REVERSE },

  // N7 — IVA assolta in altro Stato UE
  { id: 76, natura: 'N7', riferimentoNormativo: 'Art. 7-sexies lett.f-g DPR 633/1972 - servizi digitali OSS', bolloOltreSoglia: false, categoria: 'IVA assolta in altro Stato UE' },
  { id: 77, natura: 'N7', riferimentoNormativo: 'Art. 40 cc.3-4 DL 331/1993 - vendite a distanza intraUE', bolloOltreSoglia: false, categoria: 'IVA assolta in altro Stato UE' },
  { id: 78, natura: 'N7', riferimentoNormativo: 'Art. 41 c.1 lett.b DL 331/1993 - acquisti intraUE a distanza', bolloOltreSoglia: false, categoria: 'IVA assolta in altro Stato UE' },
  { id: 79, natura: 'N7', riferimentoNormativo: 'Art. 74-sexies DPR 633/1972 - servizi elettronici OSS', bolloOltreSoglia: false, categoria: 'IVA assolta in altro Stato UE' },
];

// ---------------------------------------------------------------------------
// Lookup + type guards
// ---------------------------------------------------------------------------

const NATURA_IVA_BY_ID: ReadonlyMap<number, NaturaIvaMapEntry> = new Map(
  NATURA_IVA_MAP.map((e) => [e.id, e]),
);

function buildByNatura(): ReadonlyMap<PosVatNatureCode, ReadonlyArray<NaturaIvaMapEntry>> {
  const m = new Map<PosVatNatureCode, NaturaIvaMapEntry[]>();
  for (const e of NATURA_IVA_MAP) {
    const list = m.get(e.natura);
    if (list) list.push(e);
    else m.set(e.natura, [e]);
  }
  return m;
}
const NATURA_IVA_BY_NATURA = buildByNatura();

/** True se `v` è un codice `<Natura>` SDI valido. */
export function isPosVatNatureCode(v: string): v is PosVatNatureCode {
  return (POS_VAT_NATURE_CODES as readonly string[]).includes(v);
}

/** Voce per ordinale stabile (PK). */
export function getNaturaIvaById(id: number): NaturaIvaMapEntry | undefined {
  return NATURA_IVA_BY_ID.get(id);
}

/** Voci di una natura, nell'ordine ufficiale. */
export function getNaturaIvaByNatura(natura: PosVatNatureCode): ReadonlyArray<NaturaIvaMapEntry> {
  return NATURA_IVA_BY_NATURA.get(natura) ?? [];
}

/**
 * Risolve la coppia (natura, riferimento) — chiave business del bollo.
 * Match esatto sul testo del riferimento normativo.
 */
export function findNaturaIvaPair(
  natura: PosVatNatureCode,
  riferimentoNormativo: string,
): NaturaIvaMapEntry | undefined {
  return getNaturaIvaByNatura(natura).find(
    (e) => e.riferimentoNormativo === riferimentoNormativo,
  );
}
